import axios from 'axios'
import { Message } from 'element-ui'
import router from '@/router'

/**
 * 与《素材上传404排查》一致：若误将 VUE_APP_BASE_API 设为「.../api」，
 * 与 url 中的「/api/...」会拼成 /api/api/... 导致 404。此处去掉 base 末尾的 /api。
 */
function normalizeBaseApi(base) {
  if (base == null || typeof base !== 'string') return ''
  const b = base.trim()
  if (!b) return ''
  return b.replace(/\/?api\/?$/i, '') || ''
}

/**
 * 开发环境：用局域网 IP 打开前端（如 http://192.168.31.7:3001）时，部分环境下 webpack 对 /api 的代理对 POST 会 404，
 * 此时直连「同 hostname + 后端端口」的 Spring（后端已配置 CORS）。本机用 localhost/127.0.0.1 打开时仍走相对路径 + 代理。
 * 后端非 8080 时在 .env.development 设置 VUE_APP_DEV_BACKEND_PORT。
 */
function getDevelopmentApiOrigin() {
  if (process.env.NODE_ENV !== 'development' || typeof window === 'undefined' || !window.location) {
    return ''
  }
  const { hostname, port, protocol } = window.location
  if (port !== '3001') {
    return ''
  }
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return ''
  }
  const p = (process.env.VUE_APP_DEV_BACKEND_PORT || '8080').toString().trim()
  const backendPort = p || '8080'
  return `${protocol}//${hostname}:${backendPort}`
}

const resolvedBaseURL =
  normalizeBaseApi(process.env.VUE_APP_BASE_API || '') || getDevelopmentApiOrigin()

const service = axios.create({
  baseURL: resolvedBaseURL,
  timeout: 60000
})

// 超时提示节流，避免多个请求同时超时时重复弹窗
let lastTimeoutToast = 0
const TIMEOUT_TOAST_GAP = 4000
// 经理户 Token 未配置类错误：统一简短提示并去重，避免多接口同时报错时出现多条相同红条
const NO_TOKEN_MSG_SHORT = '当前经理户暂无可用 Token，请到「主体 Token 管理」完成授权。'
let lastNoTokenToast = 0
const NO_TOKEN_TOAST_GAP = 3000
function isNoTokenError(msg) {
  if (!msg || typeof msg !== 'string') return false
  return (msg.includes('未获取到 token') || msg.includes('未获取到 Access Token')) &&
    (msg.includes('主体 Token') || msg.includes('Access Token'))
}

// 相同文案 / 功能权限类错误节流，避免多接口并发时重复弹窗
const ERROR_TOAST_GAP = 3500
const PERMISSION_TOAST_GAP = 5000
const PERMISSION_TOAST_MSG = '当前账号无该功能权限，请联系管理员配置角色菜单'
let lastErrorToastAt = 0
let lastErrorToastMsg = ''
let lastPermissionToastAt = 0

function isPermissionDeniedMsg(msg) {
  return typeof msg === 'string' && (
    msg.includes('无功能权限') ||
    msg.includes('无数据权限') ||
    msg.includes('无模块权限')
  )
}

function shouldShowErrorToast(msg) {
  const now = Date.now()
  if (isPermissionDeniedMsg(msg)) {
    if (now - lastPermissionToastAt < PERMISSION_TOAST_GAP) return false
    lastPermissionToastAt = now
    return true
  }
  if (msg === lastErrorToastMsg && now - lastErrorToastAt < ERROR_TOAST_GAP) return false
  lastErrorToastMsg = msg
  lastErrorToastAt = now
  return true
}

function showErrorToast(msg) {
  if (isPermissionDeniedMsg(msg)) {
    Message({ message: PERMISSION_TOAST_MSG, type: 'warning', duration: 4500 })
    return
  }
  Message({ message: msg, type: 'error' })
}

// 所有请求统一加上 Authorization: Bearer token（从 localStorage 的 token 读取）
service.interceptors.request.use(
  config => {
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  error => Promise.reject(error)
)

service.interceptors.response.use(
  response => {
    const res = response.data
    return res
  },
  error => {
    if (error.response && error.response.status === 401) {
      try {
        // 与登录页一致，401 时清空整套权限缓存，避免残留旧账号权限状态
        localStorage.removeItem('isSuperAdmin')
        localStorage.removeItem('allowedModules')
        localStorage.removeItem('allowedFields')
        localStorage.removeItem('canManageRoles')
        localStorage.removeItem('permissionsReady')
        localStorage.removeItem('autoCreateQaQuickappLinkEnabled')
      } catch (e) { /* ignore */ }
      localStorage.removeItem('token')
      localStorage.removeItem('allowedMenus')
      if (router.currentRoute && !router.currentRoute.meta.public) {
        router.replace('/login')
      }
      const msg = error.response?.data?.message || '未登录或登录已过期'
      Message({ message: msg + '，请重新登录', type: 'warning' })
      return Promise.reject(error)
    }
    const isTimeout = error.code === 'ECONNABORTED' || (error.message && error.message.includes('timeout'))
    if (isTimeout) {
      const msg = '请求超时，请稍后重试或尝试选择单个广告主、缩短日期范围'
      const now = Date.now()
      if (now - lastTimeoutToast > TIMEOUT_TOAST_GAP) {
        lastTimeoutToast = now
        Message({ message: msg, type: 'error', duration: 5000 })
      }
      return Promise.reject(error)
    }

    if (!error.response) {
      Message({
        message:
          '无法连接服务器（未收到后端响应）。请检查网络、HTTPS/域名、环境变量 VUE_APP_BASE_API 或 Nginx 反向代理是否指向正确后端。',
        type: 'error',
        duration: 6500
      })
      return Promise.reject(error)
    }

    let msg = error.response.data?.message || error.message || '请求失败'
    const skipErrorHandler = error.config && error.config.skipErrorHandler === true
    if (isNoTokenError(msg)) {
      msg = NO_TOKEN_MSG_SHORT
      const now = Date.now()
      if (!skipErrorHandler && now - lastNoTokenToast > NO_TOKEN_TOAST_GAP) {
        lastNoTokenToast = now
        Message({ message: msg, type: 'error' })
      }
    } else if (!skipErrorHandler && shouldShowErrorToast(msg)) {
      showErrorToast(msg)
    }
    return Promise.reject(error)
  }
)

export default service
